import { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import {
  LogOut,
  MailPlus,
  Users,
  Bot,
  User as UserIcon,
  Stethoscope,
  Phone,
  ScrollText,
  Presentation,
  Activity,
  ChevronsLeft,
  ChevronsRight,
  Home,
  Contact,
  CalendarRange,
  CalendarClock,
  Settings,
  Boxes,
} from 'lucide-react';
import defaultLogo from '../assets/icon.svg';
import { schema } from '../__generated__/schema-lite';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';

type Props = {
  open: boolean;
  onClose: () => void;
};

// Desktop-only collapse (icons-only rail). Read by NavItem/SectionLabel so we
// don't thread a prop through every link. Mobile is unaffected — it's always
// the full off-canvas drawer toggled by the hamburger.
const CollapsedCtx = createContext(false);

/** Persistent left rail (≥md) / off-canvas drawer (<md). Owns app navigation,
 *  the brand mark, and the per-user footer (email + sign-out + language). */
export function Sidebar({ open, onClose }: Props) {
  const { token, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const crud = useClientQueries(schema);
  const { data: user } = crud.$procs.me.useQuery(undefined, { enabled: !!token });
  const role = (user as { role?: string } | undefined)?.role;
  const isAdmin = role === 'ADMIN';
  const isSuperdoc = role === 'SUPERDOC';

  // Clinic branding (logo + phone) is configurable from /general-settings. Fall
  // back to the built-in defaults until a settings row exists.
  const { data: settings } = crud.clinicSettings.useFindUnique(
    { where: { id: 'singleton' } },
    { enabled: !!token },
  );
  const logoSrc =
    ((settings as { logo?: string } | undefined)?.logo as string | undefined) || defaultLogo;
  const phone =
    ((settings as { phone?: string } | undefined)?.phone as string | undefined) ||
    '+359 32 509 354';
  // The clinic name drives the browser-tab title (configurable in /general-settings;
  // falls back to the static index.html <title> until set).
  useEffect(() => {
    const n = (settings as { name?: string } | undefined)?.name?.trim();
    if (n) document.title = n;
  }, [settings]);
  // Hide the clinic phone next to the logo on the public pages (auth + legal) —
  // there's nothing to "call" there and it clutters those bare screens.
  const hidePhone = ['/login', '/register', '/about', '/privacy', '/terms', '/accept-invite'].includes(
    location.pathname,
  );

  // Desktop collapse (icons only), persisted across reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('sidebar-collapsed', collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  function signOut() {
    setToken(null);
    onClose();
    navigate('/login');
  }

  return (
    <>
      {/* Backdrop — only on mobile when the drawer is open */}
      {open ? (
        <div
          className="md:hidden fixed inset-0 bg-foreground/30 z-[55]"
          onClick={onClose}
          aria-hidden
        />
      ) : null}

      <CollapsedCtx.Provider value={collapsed}>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-[60] w-64 border-r border-border bg-card',
          'flex flex-col',
          'transform transition-all duration-200',
          // Below md: slide in/out. Above md: always docked.
          open ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0 md:static md:z-auto',
          // Desktop-only collapse to an icons-only rail.
          collapsed ? 'md:w-16' : 'md:w-64',
        )}
        aria-label="Primary"
      >
        {/* Brand */}
        <div
          className={cn(
            'h-20 px-4 flex items-center gap-2',
            collapsed && 'md:px-0 md:justify-center',
          )}
        >
          <Link
            to={token ? '/home' : '/'}
            onClick={onClose}
            className="flex items-center gap-2 font-semibold tracking-wider text-foreground"
          >
            <img src={logoSrc} alt="clinic logo" className="h-12" />
          </Link>
          {!hidePhone ? (
            <a
              href={`tel:${phone.replace(/\s+/g, '')}`}
              className={cn(
                'ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-medium leading-none text-primary hover:underline tabular-nums',
                collapsed && 'md:hidden',
              )}
            >
              <Phone className="h-3 w-3 shrink-0" />
              {phone}
            </a>
          ) : null}
        </div>

        {/* Sections */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 text-sm">
          {token && isSuperdoc ? (
            /* Read-only oversight role — a light home + contacts + calls menu. */
            <>
              <NavItem to="/home" icon={<Home className="h-4 w-4" />}>
                {t('nav.home')}
              </NavItem>
              <NavItem to="/calls" icon={<Phone className="h-4 w-4" />}>
                {t('nav.calls')}
              </NavItem>
              <NavItem to="/activity" icon={<ScrollText className="h-4 w-4" />}>
                {t('nav.activity')}
              </NavItem>

              <SectionLabel className="mt-4">{t('nav.sections.manage')}</SectionLabel>
              <NavItem to="/calendar" icon={<CalendarClock className="h-4 w-4" />}>
                {t('nav.calendar')}
              </NavItem>
              <NavItem to="/group-events" icon={<CalendarRange className="h-4 w-4" />}>
                {t('nav.groupEvents')}
              </NavItem>
              <NavItem to="/contacts" icon={<Contact className="h-4 w-4" />}>
                {t('nav.contacts')}
              </NavItem>
              <NavItem to="/superdoc/assistants" icon={<Bot className="h-4 w-4" />}>
                {t('nav.assistants')}
              </NavItem>
              <NavItem to="/admin/specialties" icon={<Stethoscope className="h-4 w-4" />}>
                {t('nav.specialties')}
              </NavItem>
              <NavItem to="/admin/services" icon={<Boxes className="h-4 w-4" />}>
                {t('nav.services')}
              </NavItem>
              <NavItem to="/general-settings" icon={<Settings className="h-4 w-4" />}>
                {t('nav.generalSettings')}
              </NavItem>

              {/* A Manager (SUPERDOC) can view the user list and create/invite
               *  users, same as an admin. Editing users stays admin-only. */}
              <SectionLabel className="mt-4">{t('nav.sections.users')}</SectionLabel>
              <NavItem to="/users" icon={<Users className="h-4 w-4" />}>
                {t('nav.users')}
              </NavItem>
              <NavItem to="/admin/create-user" icon={<MailPlus className="h-4 w-4" />}>
                {t('createUser.create')}
              </NavItem>
              <NavItem to="/admin/invite" icon={<MailPlus className="h-4 w-4" />}>
                {t('invite.send')}
              </NavItem>

              <NavItem
                to="/specialist-config"
                icon={<UserIcon className="h-4 w-4" />}
                className="mt-4"
              >
                {t('nav.myProfile')}
              </NavItem>
            </>
          ) : token ? (
            <>
              {/* Admin management group. Non-admins don't see these links — and
               *  the pages themselves redirect non-admins (defence in depth). */}
              {isAdmin ? (
                <>
                  <SectionLabel>{t('nav.sections.monitor')}</SectionLabel>
                  <NavItem to="/home" icon={<Presentation className="h-4 w-4" />}>
                    {t('nav.dashboard')}
                  </NavItem>
                  <NavItem to="/calls" icon={<Phone className="h-4 w-4" />}>
                    {t('nav.calls')}
                  </NavItem>
                  <NavItem to="/activity" icon={<ScrollText className="h-4 w-4" />}>
                    {t('nav.activity')}
                  </NavItem>
                  <NavItem to="/inngest" icon={<Activity className="h-4 w-4" />}>
                    {t('nav.inngest')}
                  </NavItem>

                  <SectionLabel className="mt-4">{t('nav.sections.manage')}</SectionLabel>
                  <NavItem to="/calendar" icon={<CalendarClock className="h-4 w-4" />}>
                    {t('nav.calendar')}
                  </NavItem>
                  <NavItem to="/group-events" icon={<CalendarRange className="h-4 w-4" />}>
                    {t('nav.groupEvents')}
                  </NavItem>
                  <NavItem to="/contacts" icon={<Contact className="h-4 w-4" />}>
                    {t('nav.contacts')}
                  </NavItem>
                  <NavItem to="/assistants" icon={<Bot className="h-4 w-4" />}>
                    {t('nav.assistants')}
                  </NavItem>
                  <NavItem to="/admin/specialties" icon={<Stethoscope className="h-4 w-4" />}>
                    {t('nav.specialties')}
                  </NavItem>
                  <NavItem to="/admin/services" icon={<Boxes className="h-4 w-4" />}>
                    {t('nav.services')}
                  </NavItem>
                  <NavItem to="/general-settings" icon={<Settings className="h-4 w-4" />}>
                    {t('nav.generalSettings')}
                  </NavItem>

                  <SectionLabel className="mt-4">{t('nav.sections.users')}</SectionLabel>
                  <NavItem to="/users" icon={<Users className="h-4 w-4" />}>
                    {t('nav.users')}
                  </NavItem>
                  <NavItem to="/admin/create-user" icon={<MailPlus className="h-4 w-4" />}>
                    {t('createUser.create')}
                  </NavItem>
                  <NavItem to="/admin/invite" icon={<MailPlus className="h-4 w-4" />}>
                    {t('invite.send')}
                  </NavItem>
                </>
              ) : null}

              {/* Specialists (non-admin) get their home + contacts here; admins
               *  have both in the Monitor group above. */}
              {!isAdmin ? (
                <>
                  <NavItem to="/home" icon={<Home className="h-4 w-4" />}>
                    {t('nav.home')}
                  </NavItem>

                  <SectionLabel className="mt-4">{t('nav.sections.manage')}</SectionLabel>
                  <NavItem to="/calendar" icon={<CalendarClock className="h-4 w-4" />}>
                    {t('nav.calendar')}
                  </NavItem>
                  <NavItem to="/group-events" icon={<CalendarRange className="h-4 w-4" />}>
                    {t('nav.groupEvents')}
                  </NavItem>
                  <NavItem to="/contacts" icon={<Contact className="h-4 w-4" />}>
                    {t('nav.contacts')}
                  </NavItem>
                </>
              ) : null}

              {/* Account — separate from the admin group, sits below it. */}
              <NavItem
                to="/specialist-config"
                icon={<UserIcon className="h-4 w-4" />}
                className={isAdmin ? 'mt-4' : undefined}
              >
                {t('nav.myProfile')}
              </NavItem>
            </>
          ) : (
            <>
              <NavItem to="/login">{t('action.signIn')}</NavItem>
              <NavItem to="/register">{t('action.signUp')}</NavItem>
            </>
          )}
        </nav>

        {/* Collapse toggle — desktop only; mobile keeps the hamburger drawer. */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          className={cn(
            'hidden md:flex items-center gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground hover:text-foreground',
            collapsed && 'md:justify-center',
          )}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4 shrink-0" />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4 shrink-0" />
              <span>{t('nav.collapse')}</span>
            </>
          )}
        </button>

        {/* Footer */}
        <div className="border-t border-border px-3 py-3 space-y-2 text-sm">
          {token && user?.email ? (
            <div
              className={cn(
                'text-muted-foreground text-xs truncate',
                collapsed && 'md:hidden',
              )}
              title={user.email as string}
            >
              {user.email as string}
            </div>
          ) : null}
          <div
            className={cn(
              'flex items-center justify-between gap-2',
              collapsed && 'md:flex-col md:items-center md:gap-3',
            )}
          >
            <ThemeSwitcher vertical={collapsed} />
            <LanguageSwitcher vertical={collapsed} />
          </div>
          {token ? (
            <button
              onClick={signOut}
              title={t('action.signOut')}
              className={cn(
                'inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground',
                collapsed && 'md:w-full md:justify-center',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className={collapsed ? 'md:hidden' : undefined}>{t('action.signOut')}</span>
            </button>
          ) : null}
        </div>
      </aside>
      </CollapsedCtx.Provider>
    </>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  const collapsed = useContext(CollapsedCtx);
  return (
    <div
      className={cn(
        'eyebrow px-3 pb-1',
        collapsed && 'md:hidden',
        className,
      )}
    >
      {children}
    </div>
  );
}

function NavItem({
  to,
  icon,
  children,
  className,
}: {
  to: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const collapsed = useContext(CollapsedCtx);
  return (
    <NavLink
      to={to}
      end
      title={collapsed && typeof children === 'string' ? children : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md p-2.5 mx-2 my-0.5 transition-colors',
          collapsed && 'md:justify-center',
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          className,
        )
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className={collapsed ? 'md:hidden' : undefined}>{children}</span>
    </NavLink>
  );
}
